import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

type Review = {
  id: string;
  stars: number;
  comment: string | null;
  review_text_es: string | null;
  review_text_en: string | null;
  display_name: string;
};

const ROTATE_MS = 6000;
const BATCH = 2;

export function LandingTestimonials() {
  const { language } = useLanguage();
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [cursor, setCursor] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("id, stars, comment, review_text_es, review_text_en, display_name")
        .eq("status", "approved")
        .gte("stars", 4)
        .order("created_at", { ascending: false })
        .limit(40);
      if (!alive) return;
      if (error || !data) {
        setReviews([]);
        return;
      }
      setReviews(data as Review[]);
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!reviews || reviews.length <= BATCH) return;
    const id = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCursor((c) => (c + BATCH) % reviews.length);
        setFade(true);
      }, 300);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [reviews]);

  // Hide entire section if no approved reviews
  if (!reviews || reviews.length === 0) return null;

  const batch: Review[] = [];
  if (reviews.length < BATCH) {
    batch.push(...reviews);
  } else {
    for (let i = 0; i < BATCH; i++) batch.push(reviews[(cursor + i) % reviews.length]);
  }

  return (
    <section
      className="py-10 px-5"
      style={{ background: "linear-gradient(160deg, #F5F3FF, #EFF6FF)" }}
    >
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#7C3AED] mb-1.5">
            {language === "es" ? "Lo que opinan algunos usuarios" : "What some users say"}
          </div>
          <h2 className="text-xl font-extrabold text-[#0F172A] mb-1.5 tracking-tight">
            {language === "es"
              ? "Resultados reales, de terapeutas reales"
              : "Real results from real therapists"}
          </h2>
        </div>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 transition-opacity duration-300"
          style={{ opacity: fade ? 1 : 0 }}
        >
          {batch.map((r, idx) => (
            <div
              key={`${r.id}-${idx}`}
              className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-sm"
            >
              <div className="flex gap-0.5 mb-2">
                {Array.from({ length: r.stars }).map((_, i) => (
                  <span key={i} className="text-[#F59E0B] text-xs">
                    ★
                  </span>
                ))}
              </div>
              <p className="text-xs text-[#334155] leading-relaxed mb-2.5 italic">
                "{(language === "es" ? r.review_text_es : r.review_text_en) || r.comment}"
              </p>
              <div className="text-[11px] font-bold text-[#0F172A]">{r.display_name}</div>
              <span className="inline-block text-[10px] text-[#16A34A] font-semibold bg-[#DCFCE7] px-2 py-0.5 rounded-full mt-1">
                {language === "es" ? "✓ Estudiante MBLearn" : "✓ MBLearn student"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
